import React, { useCallback, useMemo } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import Carousel, { CarouselProps, ControlProps } from 'nuka-carousel';
import { useIntl } from 'react-intl';

import { SocialsNetworks } from 'enums/social-networks';
import useMediaQuery from 'hooks/use-media-query';

import {
  AccountUsernameWrapper,
  CarouselContainer,
  CarouselTitle,
  Container,
  EmptyWrapper,
  ErrorMessage,
  IconFacebookVerifiedBadge,
  IconInstagramVerifiedBadge,
  IconTiktokVerifiedBadge,
  IconTwitterVerifiedBadge,
  IconYoutubeVerifiedBadge,
  Loading,
  NetworkText,
  NextButton,
  PreviousButton,
} from './styles';

interface SocialImages {
  url: string;
  description: string;
}

interface SocialCarouselProps extends CarouselProps {
  images: SocialImages[];
  network: SocialsNetworks;
  accountUsername: string;
  isLoading: boolean;
}

export const SocialCarousel = ({
  images,
  accountUsername,
  network,
  isLoading,
  ...rest
}: SocialCarouselProps) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery('mobile');

  const renderImages = useMemo(() => {
    return images.map(({ url, description }) => <img key={url} src={url} alt={description} />);
  }, [images]);

  const renderPreviousButton = useCallback(
    ({ previousSlide }: ControlProps) => (
      <PreviousButton
        onClick={previousSlide}
        aria-label={formatMessage({ id: 'backwardButton' })}
      />
    ),
    [formatMessage]
  );

  const renderNextButton = useCallback(
    ({ nextSlide }: ControlProps) => (
      <NextButton onClick={nextSlide} aria-label={formatMessage({ id: 'forwardButton' })} />
    ),
    [formatMessage]
  );

  const renderVerifiedBadgeIcon = useMemo(() => {
    switch (network) {
      case SocialsNetworks.Instagram:
        return (
          <IconInstagramVerifiedBadge data-testid="social-carousel-instagram-icon" aria-hidden />
        );

      case SocialsNetworks.Twitter:
        return <IconTwitterVerifiedBadge data-testid="social-carousel-twitter-icon" aria-hidden />;

      case SocialsNetworks.Youtube:
        return <IconYoutubeVerifiedBadge data-testid="social-carousel-youtube-icon" aria-hidden />;

      case SocialsNetworks.Tiktok:
        return <IconTiktokVerifiedBadge data-testid="social-carousel-tiktok-icon" aria-hidden />;

      case SocialsNetworks.Facebook:
        return (
          <IconFacebookVerifiedBadge data-testid="social-carousel-facebook-icon" aria-hidden />
        );

      default:
        return '';
    }
  }, [network]);

  return (
    <Container>
      {isLoading && (
        <EmptyWrapper
          aria-label={`${formatMessage({ id: 'loadingCarousel' })} ${network} ${formatMessage({
            id: 'carouselImages',
          })}`}
        >
          <Loading />
        </EmptyWrapper>
      )}
      <CarouselTitle>
        <Icon icon={network} color="icon-default" width="19px" aria-hidden />
        <AccountUsernameWrapper>
          {accountUsername}
          {renderVerifiedBadgeIcon}
        </AccountUsernameWrapper>{' '}
        {formatMessage({ id: 'on' })} <NetworkText>{network}</NetworkText>
      </CarouselTitle>
      <CarouselContainer>
        {!isLoading && images.length === 0 && (
          <EmptyWrapper>
            <ErrorMessage>{formatMessage({ id: 'carouselErrorMessage' })}</ErrorMessage>
          </EmptyWrapper>
        )}
        {!isLoading && images.length > 0 && (
          <Carousel
            {...rest}
            cellSpacing={isMobile ? 8 : 36}
            renderCenterLeftControls={renderPreviousButton}
            renderCenterRightControls={renderNextButton}
            defaultControlsConfig={{
              pagingDotsStyle: {
                fill: Styles.color.primary,
                transform: 'scale(2.5)',
                padding: '0 8px',
                top: '40px',
                position: 'relative',
              },
            }}
          >
            {renderImages}
          </Carousel>
        )}
      </CarouselContainer>
    </Container>
  );
};

export default SocialCarousel;
